import React, { useState } from "react";
import { motion } from "framer-motion";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import { site, whatsappHref } from "@/config/site";
import { euro } from "@/lib/api";
import { publicUrl } from "@/lib/publicUrl";

export const ease = [0.16, 1, 0.3, 1];

export class ErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { hasError: false }; }
  static getDerivedStateFromError() { return { hasError: true }; }
  componentDidCatch(e) { console.error("Boundary:", e); }
  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex flex-col items-center justify-center bg-background px-6 text-center">
          <p className="overline mb-3">Onoranze Funebri Pax</p>
          <h1 className="text-3xl font-serif text-primary">Si è verificato un problema</h1>
          <p className="mt-3 text-muted-foreground">Ricarichi la pagina o ci contatti al {site.telefono}.</p>
          <button onClick={() => { this.setState({ hasError: false }); window.location.href = publicUrl("/"); }}
            className="mt-6 bg-primary text-primary-foreground px-6 py-3 rounded-full">Torna alla home</button>
        </div>
      );
    }
    return this.props.children;
  }
}

export const Reveal = ({ children, delay = 0, y = 30, className = "" }) => (
  <motion.div
    className={className}
    initial={{ opacity: 0, y }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-80px" }}
    transition={{ duration: 0.9, delay, ease }}
  >
    {children}
  </motion.div>
);

export const Section = ({ id, children, className = "", alt = false }) => (
  <section
    id={id}
    className={`py-20 md:py-32 ${alt ? "bg-secondary" : "bg-background"} ${className}`}
  >
    <div className="max-w-7xl mx-auto px-6 md:px-12">{children}</div>
  </section>
);

const FALLBACK = "https://images.unsplash.com/photo-1490750967868-88aa4486c946?w=800&q=80";
export const ProductImage = ({ src, alt, className = "" }) => {
  const [err, setErr] = useState(false);
  return (
    <img
      src={err ? FALLBACK : publicUrl(src)}
      alt={alt}
      loading="lazy"
      onError={() => setErr(true)}
      className={className}
    />
  );
};

export const ProductCard = ({ p, selected, onClick, testid }) => (
  <button
    type="button"
    data-testid={testid}
    onClick={onClick}
    className={`group text-left w-full rounded-xl overflow-hidden bg-card border transition-all duration-300 hover:-translate-y-1 focus:outline-none focus:ring-2 focus:ring-accent ${
      selected ? "border-accent ring-2 ring-accent shadow-lg" : "border-border hover:shadow-md"
    }`}
  >
    <div className="aspect-[4/3] overflow-hidden bg-secondary relative">
      <ProductImage
        src={p.immagine}
        alt={p.nome}
        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
      />
      {selected && (
        <span className="absolute top-3 right-3 bg-accent text-primary text-xs font-semibold px-3 py-1 rounded-full">
          Selezionato
        </span>
      )}
    </div>
    <div className="p-3.5 md:p-5">
      <h4 className="text-base md:text-lg font-serif text-primary leading-tight">{p.nome}</h4>
      {p.materiale && <p className="text-xs text-muted-foreground mt-1">{p.materiale}{p.finitura ? ` · ${p.finitura}` : ""}</p>}
      <p className="text-xs md:text-sm text-muted-foreground mt-1.5 md:mt-2 line-clamp-2">{p.descrizione}</p>
      <p className="mt-2 md:mt-3 text-lg md:text-xl font-serif text-primary">{euro(p.prezzo)}</p>
    </div>
  </button>
);

const links = [
  { to: "#chi-siamo", label: "Chi siamo" },
  { to: "#servizi", label: "Servizi" },
  { to: "#catalogo", label: "Catalogo" },
  { to: "#contatti", label: "Contatti" },
  { to: "#faq", label: "FAQ" },
];

export const goToHash = (hash, isHome) => {
  if (isHome) document.querySelector(hash)?.scrollIntoView({ behavior: "smooth" });
  else window.location.href = `${publicUrl("/")}${hash}`;
};

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const loc = useLocation();
  const isHome = loc.pathname === "/";
  const go = (hash) => {
    setOpen(false);
    goToHash(hash, isHome);
  };
  return (
    <header className="fixed top-0 inset-x-0 z-50">
      <div className="bg-primary text-primary-foreground" data-testid="lutto-banner">
        <div className="max-w-7xl mx-auto px-4 md:px-12 py-2 flex items-center justify-center gap-2 text-xs sm:text-sm text-center">
          <span className="text-white/80 hidden sm:inline">{site.bannerTesto}</span>
          <span className="text-white/80 sm:hidden">Lutto? Siamo disponibili 24/7</span>
          <a href={`tel:${site.telefonoRaw}`} className="font-semibold text-accent hover:underline whitespace-nowrap" data-testid="banner-phone">
            {site.telefono}
          </a>
        </div>
      </div>
      <div className="backdrop-blur-xl bg-white/85 border-b border-black/5">
        <div className="max-w-7xl mx-auto px-6 md:px-12 h-16 md:h-20 flex items-center justify-between">
          <Link to="/" data-testid="nav-logo" className="flex items-center gap-3">
            <img src={site.logo} alt={site.nome} className="h-11 md:h-14 w-auto object-contain" />
          </Link>
          <nav className="hidden lg:flex items-center gap-8">
            {links.map((l) => (
              <button key={l.to} data-testid={`nav-${l.label}`} onClick={() => go(l.to)}
                className="text-sm text-muted-foreground hover:text-primary transition-colors">
                {l.label}
              </button>
            ))}
            <a href={`tel:${site.telefonoRaw}`} data-testid="nav-phone"
              className="inline-flex items-center gap-2 text-sm font-semibold text-primary hover:text-accent transition-colors">
              <Phone size={16} /> {site.telefono}
            </a>
            <Link to="/preventivo" data-testid="nav-preventivo"
              className="text-sm font-semibold bg-primary text-primary-foreground px-5 py-2.5 rounded-full hover:bg-primary/90 transition-colors">
              Richiedi preventivo
            </Link>
          </nav>
          <div className="lg:hidden flex items-center gap-1">
            <a href={`tel:${site.telefonoRaw}`} className="p-2 text-primary" aria-label={`Chiama ${site.telefono}`} data-testid="nav-phone-mobile">
              <Phone size={22} />
            </a>
            <button className="text-primary p-2" onClick={() => setOpen(!open)} data-testid="nav-toggle" aria-label="Menu">
              {open ? <X /> : <Menu />}
            </button>
          </div>
        </div>
        {open && (
          <div className="lg:hidden bg-white border-t border-border px-6 py-4 space-y-3">
            {links.map((l) => (
              <button key={l.to} onClick={() => go(l.to)} className="block w-full text-left py-2 text-primary">{l.label}</button>
            ))}
            <a href={`tel:${site.telefonoRaw}`} className="flex items-center gap-2 py-2 font-semibold text-primary">
              <Phone size={18} /> {site.telefono}
            </a>
            <Link to="/preventivo" onClick={() => setOpen(false)} className="block bg-primary text-primary-foreground text-center py-3 rounded-full font-semibold">
              Richiedi preventivo
            </Link>
          </div>
        )}
      </div>
    </header>
  );
};

export const Footer = () => {
  const loc = useLocation();
  const isHome = loc.pathname === "/";
  return (
    <footer className="bg-primary text-primary-foreground grain relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 grid gap-10 md:grid-cols-4 relative">
        <div className="md:col-span-2">
          <h3 className="text-3xl font-serif">{site.nome}</h3>
          <p className="mt-4 text-white/70 max-w-sm leading-relaxed">{site.descrizione}</p>
          <p className="mt-6 text-sm text-accent">{site.orari}</p>
        </div>
        <div>
          <p className="overline mb-4">Contatti</p>
          <ul className="space-y-2 text-white/80 text-sm">
            <li>
              <a href={`tel:${site.telefonoRaw}`} className="inline-flex items-center gap-2 hover:text-accent">
                <Phone size={15} /> {site.telefono}
              </a>
            </li>
            <li><a href={`mailto:${site.email}`} className="hover:text-accent">{site.email}</a></li>
            <li>{site.indirizzo}</li>
            <li>
              <a href={whatsappHref()} target="_blank" rel="noreferrer" className="hover:text-accent">WhatsApp</a>
            </li>
          </ul>
        </div>
        <div>
          <p className="overline mb-4">Link</p>
          <ul className="space-y-2 text-white/80 text-sm">
            <li><button type="button" onClick={() => goToHash("#servizi", isHome)} className="hover:text-accent">Servizi</button></li>
            <li><button type="button" onClick={() => goToHash("#catalogo", isHome)} className="hover:text-accent">Catalogo</button></li>
            <li><Link to="/preventivo" className="hover:text-accent">Preventivatore</Link></li>
            <li><Link to="/privacy" className="hover:text-accent">Privacy</Link></li>
            <li><Link to="/cookie" className="hover:text-accent">Cookie</Link></li>
          </ul>
        </div>
      </div>
      <div className="border-t border-white/10 py-6 text-center text-xs text-white/50">
        © {new Date().getFullYear()} {site.nome} · {site.piva} · Tutti i diritti riservati
      </div>
    </footer>
  );
};

export const CookieBanner = () => {
  const loc = useLocation();
  const [visible, setVisible] = useState(() => {
    try { return localStorage.getItem("pax_cookie") !== "1"; }
    catch { return true; }
  });
  if (!visible || loc.pathname.startsWith("/admin")) return null;
  const accept = () => {
    try { localStorage.setItem("pax_cookie", "1"); } catch { /* ignore */ }
    setVisible(false);
  };
  return (
    <div className="fixed bottom-24 lg:bottom-6 inset-x-4 md:inset-x-auto md:left-6 md:max-w-md z-50 bg-primary text-primary-foreground rounded-xl p-4 border border-white/10" data-testid="cookie-banner">
      <p className="text-sm text-white/85 leading-relaxed">
        Usiamo cookie tecnici e, previo consenso, strumenti di misurazione. Maggiori informazioni nella{" "}
        <Link to="/cookie" className="text-accent underline underline-offset-2">informativa cookie</Link>.
      </p>
      <div className="mt-3 flex gap-2">
        <button type="button" onClick={accept} className="bg-accent text-primary text-sm font-semibold px-4 py-2 rounded-full">
          Accetto
        </button>
        <Link to="/privacy" className="text-sm text-white/70 px-3 py-2 hover:text-white">Privacy</Link>
      </div>
    </div>
  );
};
